import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../../../api/axios';
import {
  LayoutDashboard,
  Briefcase,
  FileText,
  Users,
  BarChart3,
  X,
  Layout,
  ClipboardList,
  CheckSquare,
  IndianRupee,
  CreditCard,
  Package,
  Shield,
  Building2,
  Scale,
  Ruler,
  ChevronLeft,
  ChevronRight,
  Menu,
  Truck,
  UserRound,
  FolderKanban,
  Award,
  DraftingCompass,
  UserCog,
  UsersRound,
  Contact,
  ClipboardType,
  Database,
  ChevronDown,
  ChevronUp,
  Settings,
  Wallet,
  Tag,
  Calendar,
  Clock,
  Clock9,
  AlertCircle,
  DollarSign,
  ShoppingCart,
  Landmark,
} from 'lucide-react';
import styles from './Sidebar.module.css';
import { useSidebar } from '../../../context/SidebarContext';

const Sidebar = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isCollapsed, toggleCollapse, sidebarScrollPos, setSidebarScrollPos } = useSidebar();
  const navRef = React.useRef(null);
  const [user, setUser] = React.useState(() => JSON.parse(localStorage.getItem('user') || '{}'));

  // Restore scroll position
  useEffect(() => {
    if (navRef.current) {
      navRef.current.scrollTop = sidebarScrollPos;
    }
  }, []);

  const handleScroll = (e) => {
    setSidebarScrollPos(e.target.scrollTop);
  };

  const allMenuItems = React.useMemo(() => [

    { icon: <LayoutDashboard size={20} />, label: 'Dashboard', path: '/', permission: 'view dashboard' },

    //Employee Side View
    { icon: <LayoutDashboard size={20} />, label: 'Employee Dashboard', path: '/employees/dashboard', activePaths: ['/employees/dashboard'], permission: 'view dashboard' },
    { icon: <ClipboardList size={20} />, label: 'Employee Attendance', path: '/employees/my-attendance', activePaths: ['/employees/my-attendance'], permission: 'view employee attendance' },
    { icon: <Clock9 size={20} />, label: 'My Overtime', path: '/employees/my-overtime', activePaths: ['/employees/my-overtime'], permission: 'view ot employee' },
    { icon: <FileText size={20} />, label: 'Employee Salary Slip', path: '/employees/my-salary-slips', activePaths: ['/employees/my-salary-slips'], permission: 'view employee salary' },



    // /tasks, /tasks/create, /tasks/edit/:id, /tasks/view/:id
    { icon: <ClipboardList size={20} />, label: 'Tasks', path: '/tasks', activePaths: ['/tasks/'], permission: 'view tasks' },

    // /projects, /projects/create, /projects/edit/:id, /projects/:id
    { icon: <FolderKanban size={20} />, label: 'Projects', path: '/projects', activePaths: ['/projects/create', '/projects/edit/'], permission: 'view projects' },

    // /projects/work-orders, /projects/work-orders/:id, /projects/:id/work-orders
    { icon: <ClipboardList size={18} />, label: 'Work Orders', path: '/projects/work-orders', activePaths: ['/projects/work-orders/'], permission: 'manage work orders' },

    // /projects/lois, /purchase-orders, /purchase-orders/create, /purchase-orders/edit/:id, /purchase-orders/:id
    { icon: <FileText size={18} />, label: 'LOIs (Letter of Intent)', path: '/projects/lois', activePaths: ['/projects/lois/'], permission: 'view letter of intent' },


    // /projects/design-submissions, /upload, /edit/:id, /review/:id, /design-approval/:id
    { icon: <DraftingCompass size={18} />, label: 'Design Submissions', path: '/projects/design-submissions', activePaths: ['/projects/design-submissions/upload', '/projects/design-submissions/edit/', '/projects/design-submissions/review/', '/projects/design-approval/'], permission: 'view designs' },
    { icon: <ShoppingCart size={18} />, label: 'Purchase Order', path: '/purchase-orders', activePaths: ['/purchase-orders/'], permission: 'view purchase orders' },
    { icon: <Award size={18} />, label: 'Manufacturer Clearance', path: '/manufacturer-clearance', activePaths: ['/manufacturer-clearance/'], permission: 'view manufacturer clearances' },
    // /inspections, /inspections/schedule, /inspections/edit/:id, /inspections/clearance/:id, /inspections/:id
    { icon: <CheckSquare size={18} />, label: 'Inspections', path: '/inspections', activePaths: ['/inspections/'], permission: 'view inspections' },



    // /receipts, /receipts/add, /receipts/edit/:id, /receipts/view/:id
    { icon: <Package size={18} />, label: 'Material Receipts', path: '/receipts', activePaths: ['/receipts/'], permission: 'view material receipts' },
    { icon: <FileText size={20} />, label: 'Sales Invoices', path: '/invoices', activePaths: ['/invoices/'], permission: 'view sales invoices' },
    {
      label: 'Finance',
      icon: <IndianRupee size={20} />,
      isCategory: true,
      subItems: [
        // /payment/vendor-payments, /payment/vendor-payments/:id
        { icon: <CreditCard size={20} />, label: 'Vendor Payment', path: '/payment/vendor-payments', activePaths: ['/payment/vendor-payments/'], permission: 'view vendor payments' },
        // /payment/payments-received, /payment/payments-received/view/:id, /add, /edit/:id
        { icon: <IndianRupee size={20} />, label: 'Payment Received', path: '/payment/payments-received', activePaths: ['/payment/payments-received/'], permission: 'view payments received' },
        // /expenses
        { icon: <Wallet size={20} />, label: 'Expenses', path: '/expenses', activePaths: ['/expenses/'], permission: 'view expenses' },
      ]
    },

    // /vendors, /vendors/view/:id
    { icon: <Truck size={20} />, label: 'Vendors', path: '/vendors', activePaths: ['/vendors/view/'], permission: 'view vendors' },

    // /customers, /customers/view/:id
    { icon: <UserRound size={20} />, label: 'Customers', path: '/customers', activePaths: ['/customers/view/'], permission: 'view customers' },
    {
      label: 'Masters',
      icon: <Database size={20} />,
      isCategory: true,
      subItems: [
        // /employees/departments, /employees/departments/view/:id
        { icon: <Building2 size={20} />, label: 'Departments', path: '/employees/departments', activePaths: ['/employees/departments/view/'], permission: 'view departments' },
        // /employees/designations, /employees/designations/view/:id
        { icon: <Award size={20} />, label: 'Designations', path: '/employees/designations', activePaths: ['/employees/designations/view/'], permission: 'view designations' },
        // /units, /units/view/:id
        { icon: <Ruler size={18} />, label: 'Units', path: '/units', activePaths: ['/units/view/'], permission: 'manage inventory' },
        // /taxes, /taxes/view/:id
        { icon: <Scale size={18} />, label: 'Taxes', path: '/taxes', activePaths: ['/taxes/view/'], permission: 'manage finance' },
        // /project-types, /project-types/view/:id
        { icon: <ClipboardType size={20} />, label: 'Project Types', path: '/project-types', activePaths: ['/project-types/view/'], permission: 'view project types' },
        // /banks, /banks/view/:id
        { icon: <Building2 size={18} />, label: 'Banks', path: '/banks', activePaths: ['/banks/view/'], permission: 'manage finance' },
        // /finance/expense-categories
        { icon: <Tag size={18} />, label: 'Expense Category', path: '/finance/expense-categories', activePaths: ['/finance/expense-categories/'], permission: 'view expenses' },
        // /holidays
        { icon: <Calendar size={18} />, label: 'Holidays', path: '/holidays', activePaths: ['/holidays/'], permission: 'view holidays' },
        // /leave-types
        { icon: <ClipboardList size={18} />, label: 'Leave Types', path: '/leave-types', activePaths: ['/leave-types/'], permission: 'view leave types' },
      ]
    },
    {
      label: 'HR',
      icon: <UsersRound size={20} />,
      isCategory: true,
      subItems: [
        { icon: <LayoutDashboard size={20} />, label: 'HR Dashboard', path: '/hr/dashboard', activePaths: ['/hr/dashboard'], permission: 'view hr dashboard' },

        // /employees, /employees/view/:id
        { icon: <Contact size={20} />, label: 'Employees', path: '/employees', activePaths: ['/employees/view/'], permission: 'view employees' },
        { icon: <Shield size={20} />, label: 'Employee Permissions', path: '/user-permissions', permission: 'manage permissions' },
        { icon: <Calendar size={20} />, label: 'Manage Leave', path: '/employees/leave-requests', activePaths: ['/employees/leave-requests/'], permission: 'view leave' },
        { icon: <ClipboardList size={20} label="Attendance" />, label: 'Attendance', path: '/employees/attendance', activePaths: ['/employees/attendance/'], permission: 'view attendance' },
        { icon: <FileText size={20} />, label: 'Monthly Report', path: '/employees/monthly-report', activePaths: ['/employees/monthly-report'], permission: 'view attendance' },
        { icon: <Settings size={20} />, label: 'Attendance Settings', path: '/employees/attendance-settings', activePaths: ['/employees/attendance-settings'], permission: 'manage permissions' },

        { icon: <Clock9 size={20} />, label: 'OT Assignments', path: '/employees/ot-assignments', activePaths: ['/employees/ot-assignments/'], permission: 'view ot admin' },
        { icon: <DollarSign size={20} />, label: 'Advance Requests', path: '/employees/advance-requests', activePaths: ['/employees/advance-requests/'], permission: 'view advance requests' },
        { icon: <Landmark size={20} />, label: 'Loan Requests', path: '/employees/loan-requests', activePaths: ['/employees/loan-requests/'], permission: 'view loan requests' },
        { icon: <FileText size={20} />, label: 'Payroll / Salary', path: '/employees/payroll', activePaths: ['/employees/payroll'], permission: 'view payroll' },
      ]
    },
  ], []);

  // Initialize expandedMenus state synchronously based on current location to avoid first-render flash
  const [expandedMenus, setExpandedMenus] = React.useState(() => {
    const currentPath = window.location.pathname;
    const activeCategory = allMenuItems.find(item =>
      item.isCategory && item.subItems?.some(sub => sub.path === currentPath || (sub.activePaths && sub.activePaths.some(p => currentPath.startsWith(p))))
    );
    return activeCategory ? [activeCategory.label] : [];
  });

  const userRole = user.role || '';
  const userPermissions = user.permissions || [];
  const isAdmin = userRole.toLowerCase() === 'admin';

  // Toggle category expansion
  const toggleMenu = (label) => {
    setExpandedMenus(prev =>
      prev.includes(label)
        ? prev.filter(item => item !== label)
        : [...prev, label]
    );
  };


  // Background sync permissions
  useEffect(() => {
    const syncUser = async () => {
      try {
        const response = await api.get('/user');
        if (response.data.success) {
          const freshUser = response.data.data;
          // Update localStorage and state if something changed
          if (freshUser && freshUser.permissions && JSON.stringify(freshUser.permissions) !== JSON.stringify(user.permissions)) {
            localStorage.setItem('user', JSON.stringify(freshUser));
            setUser(freshUser);
          }
        }
      } catch (err) {
        console.error('Failed to sync user permissions:', err);
      }
    };

    syncUser();
  }, [user.permissions]);

  // Admins see all items; others only see permitted ones or items with no permission requirement
  const filterMenuItems = (items) => {
    return items.filter(item => {
      if (isAdmin) return true;

      // For non-Admin role, hide the regular Dashboard
      if (!isAdmin && item.label === 'Dashboard' && !item.isCategory) {
        return false;
      }

      // For non-Admin role, keep Employee Dashboard
      if (!isAdmin && item.label === 'Employee Dashboard') {
        return true;
      }

      // For categories, check if any sub-item is permitted
      if (item.isCategory && item.subItems) {
        const permittedSubItems = filterMenuItems(item.subItems);
        return permittedSubItems.length > 0;
      }

      return !item.permission || userPermissions.includes(item.permission);
    }).map(item => {
      // Transform Employee Dashboard for non-Admin role
      if (!isAdmin && item.label === 'Employee Dashboard' && item.isCategory) {
        return {
          icon: item.icon,
          label: item.label,
          path: '/employees/dashboard',
          permission: 'view dashboard'
        };
      }

      if (item.isCategory && item.subItems) {
        return { ...item, subItems: filterMenuItems(item.subItems) };
      }
      return item;
    });
  };

  useEffect(() => {
    // Check user on app load and redirect accordingly
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const currentPath = window.location.pathname;
    const isUserAdmin = (user.role || '').toLowerCase() === 'admin';

    // If user is logged in and at root path
    if (user && user.id && (currentPath === '/' || currentPath === '')) {
      if (!isUserAdmin) {
        navigate('/employees/dashboard', { replace: true });
      }
      // Admin stays at '/' (Dashboard)
    }
  }, [navigate]);

  const menuItems = filterMenuItems(allMenuItems);

  // Auto-expand logic when navigating
  useEffect(() => {
    const currentPath = location.pathname;
    const activeCategory = allMenuItems.find(item =>
      item.isCategory && item.subItems?.some(sub => sub.path === currentPath || (sub.activePaths && sub.activePaths.some(p => currentPath.startsWith(p))))
    );

    if (activeCategory) {
      // Expand the active category if it's not already open
      setExpandedMenus(prev => {
        if (!prev.includes(activeCategory.label)) {
          return [...prev, activeCategory.label];
        }
        return prev;
      });
    }
  }, [location.pathname, allMenuItems]);

  const handleNavigation = (path) => {
    navigate(path);
    if (onClose) onClose();
  };

  const isItemActive = (path, activePaths) => {
    const loc = location.pathname;
    if (loc === path) return true;
    if (activePaths && activePaths.some(p => loc.startsWith(p))) return true;
    // Handle /projects/:id detail pages — activate Projects menu
    // but NOT if the path belongs to a deeper sub-module
    if (path === '/projects' && loc.startsWith('/projects/')) {
      const subModules = [
        '/projects/work-orders',
        '/projects/lois',
        '/projects/design-submissions',
        '/projects/design-approval',
        '/projects/create',
        '/projects/edit/',
      ];
      if (!subModules.some(sub => loc.startsWith(sub))) return true;
    }
    // Handle /employees/:id — activate Employees, not Departments/Designations
    if (path === '/employees' && loc.startsWith('/employees/view/')) return true;
    return false;
  };

  return (
    <>
      <div
        className={`${styles.overlay} ${isOpen ? styles.open : ''}`}
        onClick={onClose}
        aria-hidden="true"
      />
      <aside className={`${styles['sidebar']} ${isOpen ? styles['open'] : ''} ${isCollapsed ? styles['collapsed'] : ''}`}>
        <div className={styles['sidebar-header']}>
          <div className={styles['sidebar-logo']}>
            <div className={styles['logo-icon']}>HE</div>
            {!isCollapsed && (
              <div className={styles['logo-text']}>
                <span className={styles['brand-name']}>HARI OM</span>
                <span className={styles['brand-sub']}>ELECTRICALS</span>
              </div>
            )}
          </div>
          <button className={styles['collapse-btn']} onClick={toggleCollapse}>
            {isCollapsed ? <Menu size={20} /> : <ChevronLeft size={20} />}
          </button>
          <button className={styles['sidebar-close-btn']} onClick={onClose}>
            <X size={24} />
          </button>
        </div>

        <nav className={styles['sidebar-nav']} ref={navRef} onScroll={handleScroll}>
          {menuItems.map((item, index) => {
            if (item.isCategory) {
              const isExpanded = expandedMenus.includes(item.label);
              const isChildActive = item.subItems.some(sub => isItemActive(sub.path, sub.activePaths));

              return (
                <div key={index} className={styles['category-group']}>
                  <div
                    className={`${styles['nav-item']} ${isChildActive && !isExpanded ? styles['active-category'] : ''}`}
                    onClick={() => toggleMenu(item.label)}
                    style={{ cursor: 'pointer' }}
                    title={isCollapsed ? item.label : ''}
                  >
                    <div className={styles['nav-item-content']}>
                      {item.icon}
                      {!isCollapsed && <span>{item.label}</span>}
                    </div>
                    {!isCollapsed && (
                      <div className={styles['expand-icon']}>
                        {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                      </div>
                    )}
                  </div>
                  {!isCollapsed && isExpanded && (
                    <div className={styles['sub-menu']}>
                      {item.subItems.map((subItem, subIndex) => (
                        <div
                          key={subIndex}
                          className={`${styles['nav-item']} ${styles['sub-nav-item']} ${isItemActive(subItem.path, subItem.activePaths) ? styles['active'] : ''}`}
                          onClick={() => handleNavigation(subItem.path)}
                          style={{ cursor: 'pointer' }}
                        >
                          <div className={styles['nav-item-content']}>
                            <span>{subItem.label}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            }

            return (
              <div key={index} className={styles['menu-group']}>
                <div
                  className={`${styles['nav-item']} ${isItemActive(item.path, item.activePaths) ? styles['active'] : ''}`}
                  onClick={() => handleNavigation(item.path)}
                  style={{ cursor: 'pointer' }}
                  title={isCollapsed ? item.label : ''}
                >
                  <div className={styles['nav-item-content']}>
                    {item.icon}
                    {!isCollapsed && <span>{item.label}</span>}
                  </div>
                </div>
              </div>
            );
          })}
        </nav>
      </aside>
    </>
  );
};

export default Sidebar;
